import { Avatar } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ITicketSupport } from '../../interfaces/apis';
import styled from 'styled-components';
import Text from 'components/Text';
import Box from 'components/Box';
import { IcAttach, IcCalender, IcChange, IcFlag, IcHigh, IcNext, IcTime } from 'components/Icon/icons';
import { ERequestSupportStatus, RequestPriorityConfig, RequestSupportStatusConfig } from 'pages/request-support/services/constants';


interface TicketCardProps {
  ticket: ITicketSupport;
  handleOpenChangeAssignModal?: (assignId: string, ticketId: string) => void;
  onOpenDetailRequestModal?: (ticketId: string) => void;
  status?: ERequestSupportStatus;
}
const TicketCard = ({ ticket, handleOpenChangeAssignModal, onOpenDetailRequestModal, status }: TicketCardProps) => {

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ticket.id,
    data: {
      ticket,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
  };

  return (
    <Container
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        ...style,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <ContentCard 
        onClick={() => onOpenDetailRequestModal?.(ticket.id)} 
        $statusColor={RequestSupportStatusConfig[status || 'NEW']?.color}
      >
        <Box flex={1} display='flex' flexDirection='column' style={{ gap: 6 }}>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Text variant='BODY_2' className='business-name' color='blue'>
              {ticket.businessName}
            </Text>
            <IconChange>
              <IcChange />
            </IconChange>
          </Box>

          <Text variant='BODY_1' className='request-content'>
            <div dangerouslySetInnerHTML={{ __html: ticket.description }} />
          </Text>
        </Box>
        <Box display='flex' justifyContent='space-between' alignItems='center' style={{ borderTop: '1px solid #F6F7FC', paddingTop: 8 }}>
          <DateBox>
            <PriorityBox style={{ backgroundColor: RequestPriorityConfig[ticket.priority].background}}>
              {ticket.priority === 'HIGH' && <IcHigh />}
              {ticket.priority === 'MEDIUM' && <IcFlag />}
              {ticket.priority === 'LOW' && <IcTime />}
              <Text variant='CAPTION_3' style={{ color: RequestPriorityConfig[ticket.priority].color, lineHeight: '1' }}>
                {ticket.priority}
              </Text>
            </PriorityBox>
            <Box display='flex' alignItems='center' style={{ gap: 4 }} className='date-box'>
              <IcCalender />
              <Text variant='CAPTION_3' color='text_2'>
               {ticket.requestDate.split('-').slice(0, 2).join('-')}
              </Text>
            </Box>
            <Box display='flex' alignItems='center' style={{ gap: 4 }} className='date-box'>
              <IcAttach />
              <Text variant='CAPTION_3' color='text_2'>
                {ticket.totalAttachment}
              </Text>
            </Box>
          </DateBox>
          <Assignment
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleOpenChangeAssignModal?.(ticket.assignId, ticket.id);
            }}
          >
            <Avatar size={24} src={ticket.avatar} style={{ fontSize: 12 }}>
              {ticket.assignName?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Text variant='CAPTION_3' className='assign-name'>
              {ticket.assignName}
            </Text>
            <IcNext />
          </Assignment>
        </Box>
      </ContentCard>
    </Container>
  );
};

export default TicketCard;

const Container = styled.div`
  position: relative;
  margin-left: 6px;
`;

const ContentCard = styled.div<{ $statusColor?: string }>`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 8px;
  border: 1px solid #f6f7fc;
  background: #fff;
  border: 1px solid #F1F5F9;
  box-shadow: 0 2px 10px -2px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -5px;
    width: 100%;
    height: 100%;
    border-left: 5px solid ${({ $statusColor }) => $statusColor || '#E5E7EB'};
    border-radius: 10px;
    pointer-events: none;
  }

  &:hover {
    box-shadow: 0 10px 24px -2px rgba(15, 23, 42, 0.08);
  }

  .business-name {
    font-family: 'Poppins';
  }

  .request-content {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;

    & > div > p {
      margin: 0;
    }
  }
`;

const DateBox = styled.div`
  display: flex;
  gap: 8px;

  .date-box {
    padding: 2px;
    background: #F6F7FC;
    border-radius: 4px;
  }
`;

const IconChange = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: #f6f7fc;
`;

const Assignment = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;

  .assign-name {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    width: fit-content;
  }
`;

const PriorityBox = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  border-radius: 4px;
`;